import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Upload, Trash2, CheckCircle2, AlertTriangle, Users, Save } from "lucide-react";

export default function AdminCampaigns() {
  const [settings, setSettings] = useState(null);
  const [recips, setRecips] = useState({ recipients: [], total: 0, subscribed: 0, unsubscribed: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [importText, setImportText] = useState("");
  const [subject, setSubject] = useState("NC Legal Forms — every NC court form, fillable online");
  const [preheader, setPreheader] = useState("Fill, AI-complete and download official NC court forms in minutes.");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState("");

  const loadAll = () => {
    api.get("/admin/campaign/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/campaign/recipients").then(({ data }) => setRecips(data)).catch(() => {});
    api.get("/admin/campaign/list").then(({ data }) => setCampaigns(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    setBusy("settings");
    try { const { data } = await api.put("/admin/campaign/settings", settings); setSettings(data); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings"); }
    setBusy("");
  };
  const doImport = async () => {
    if (!importText.trim()) { toast.error("Paste some recipients first"); return; }
    setBusy("import");
    try {
      const { data } = await api.post("/admin/campaign/recipients/import", { text: importText });
      toast.success(`Imported ${data.added} · skipped ${data.skipped} duplicates`);
      setImportText(""); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    setBusy("");
  };
  const del = async (id) => { await api.delete(`/admin/campaign/recipients/${id}`); loadAll(); };
  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a test email"); return; }
    setBusy("test");
    try { await api.post("/admin/campaign/test", { email: testEmail }); toast.success(`Test sent to ${testEmail}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send test"); }
    setBusy("");
  };
  const sendAll = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/admin/campaign/send", { subject, preheader });
      toast.success(`Campaign started to ${data.recipient_count} recipients`);
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start campaign"); }
    setBusy("");
  };

  const configured = settings?.sending_configured;
  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div>
        <h1 className="font-serif text-3xl tracking-tight flex items-center gap-2"><Mail size={24} className="text-accent" /> Attorney Campaigns</h1>
        <p className="text-sm text-muted-foreground mt-1">Market NC Legal Forms to your attorney list — fully CAN-SPAM compliant (unsubscribe + address in every email).</p>
      </div>

      {settings && (
        <div className={`flex items-start gap-3 p-4 border-2 ${configured ? "border-emerald-600 bg-emerald-50" : "border-amber-500 bg-amber-50"}`} data-testid="sending-status">
          {configured ? <CheckCircle2 className="text-emerald-600 shrink-0" size={20} /> : <AlertTriangle className="text-amber-600 shrink-0" size={20} />}
          <p className="text-sm">
            {configured
              ? "Email sending is configured. Test sends and campaigns will be delivered via Resend."
              : "Sending is not configured yet. Add RESEND_API_KEY and a verified SENDER_EMAIL to the backend .env, then restart the backend. You can still build your list and settings now."}
          </p>
        </div>
      )}

      {/* Sender settings */}
      {settings && (
        <div className="grid-card p-6" data-testid="campaign-settings-card">
          <p className="overline text-accent mb-4">Sender & compliance</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["from_name", "From name"], ["from_email", "From email (verified in Resend)"], ["reply_to", "Reply-to (optional)"], ["business_name", "Business name"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs">{l}</Label>
                <Input data-testid={`cs-${k}`} value={settings[k] || ""} onChange={(e) => setS(k, e.target.value)} className="rounded-sm mt-1" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs">Business mailing address (required in every email)</Label>
              <Input data-testid="cs-business_address" value={settings.business_address || ""} onChange={(e) => setS("business_address", e.target.value)} className="rounded-sm mt-1" />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={busy === "settings"} data-testid="save-campaign-settings" className="rounded-sm gap-2 mt-4 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
            <Save size={16} /> {busy === "settings" ? "Saving…" : "Save settings"}
          </Button>
        </div>
      )}

      {/* Recipients */}
      <div className="grid-card p-6" data-testid="recipients-card">
        <div className="flex items-center justify-between mb-4">
          <p className="overline text-accent">Recipients</p>
          <div className="flex gap-2 text-xs">
            <Badge className="rounded-sm bg-emerald-600"><Users size={12} className="mr-1" /> {recips.subscribed} subscribed</Badge>
            <Badge variant="outline" className="rounded-sm">{recips.unsubscribed} unsubscribed</Badge>
          </div>
        </div>
        <Label className="text-xs">Import a list you own or license — one per line: <span className="font-mono">email, name, firm</span></Label>
        <Textarea data-testid="import-textarea" value={importText} onChange={(e) => setImportText(e.target.value)} rows={4}
          className="rounded-sm mt-1 font-mono text-xs" placeholder={"jane@firm.com, Jane Doe, Doe Law\nbob@legal.com, Bob Smith, Smith LLP"} />
        <Button onClick={doImport} disabled={busy === "import"} data-testid="import-recipients-btn" className="rounded-sm gap-2 mt-3 bg-accent text-accent-foreground hover:bg-accent/90">
          <Upload size={16} /> {busy === "import" ? "Importing…" : "Import recipients"}
        </Button>

        {recips.recipients.length > 0 && (
          <div className="mt-5 max-h-64 overflow-y-auto border border-border" data-testid="recipients-list">
            {recips.recipients.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-3 py-2 border-b border-border/60 text-sm" data-testid={`recipient-${r.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${r.status === "subscribed" ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{r.email}</span>
                  <span className="text-muted-foreground"> {r.name ? `· ${r.name}` : ""}{r.firm ? ` · ${r.firm}` : ""}</span>
                </div>
                {r.status === "unsubscribed" && <span className="text-[0.65rem] uppercase text-muted-foreground">unsubscribed</span>}
                <button onClick={() => del(r.id)} data-testid={`del-recipient-${r.id}`} className="text-muted-foreground hover:text-accent"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose & send */}
      <div className="grid-card p-6" data-testid="compose-card">
        <p className="overline text-accent mb-4">Compose & send</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Subject line</Label>
            <Input data-testid="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs">Preheader (inbox preview text)</Label>
            <Input data-testid="campaign-preheader" value={preheader} onChange={(e) => setPreheader(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <p className="text-xs text-muted-foreground">The email body is a polished template showcasing your features and current pricing ($39/mo · $390/yr · $3.95 per-form) with a "Start free trial" button.</p>

          <div className="flex flex-wrap items-end gap-3 pt-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Send a test to</Label>
              <Input data-testid="test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="rounded-sm mt-1" />
            </div>
            <Button onClick={sendTest} disabled={busy === "test"} data-testid="send-test-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
              <Send size={15} /> {busy === "test" ? "Sending…" : "Send test"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="send-all-btn" className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                  <Send size={16} /> Send to {recips.subscribed} recipients
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Send this campaign?</AlertDialogTitle>
                  <AlertDialogDescription>This emails all {recips.subscribed} subscribed recipients. Make sure you've sent yourself a test first.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="send-all-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={sendAll} data-testid="send-all-confirm" className="bg-accent text-accent-foreground hover:bg-accent/90">Send campaign</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Past campaigns */}
      {campaigns.length > 0 && (
        <div className="grid-card p-6" data-testid="campaigns-history">
          <p className="overline text-accent mb-4">Sent campaigns</p>
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-border/60 text-sm">
                <div className="min-w-0"><p className="font-medium truncate">{c.subject}</p><p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</p></div>
                <div className="text-xs text-right shrink-0 ml-3">
                  <Badge className="rounded-sm bg-emerald-600">{c.sent} sent</Badge>
                  {c.failed > 0 && <Badge variant="outline" className="rounded-sm ml-1">{c.failed} failed</Badge>}
                  <span className="block text-muted-foreground mt-1">{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
